module Hereabout.UI.Components.VenuePreviewComponent where

import Prelude

import Data.Array as A
import Data.Maybe (Maybe(..))
import Data.String as S
import Effect.Class (class MonadEffect, liftEffect)
import Halogen as H
import Halogen.HTML as HH
import Halogen.HTML.Events as HE
import Halogen.HTML.Properties as HP
import Hereabout.Data.Route (Route(..), routeCodec)
import Hereabout.Effects (class Navigate, navigate)
import Hereabout.Types (Address(..), Venue(..))
import Hereabout.UI.Bits (renderIcon)
import Hereabout.UI.Bulma.Classes as Bulma
import Hereabout.UI.Components.ContentComponent as ContentComp
import Hereabout.UI.Components.EventPreviewComponent as EventPrvComp
import Routing.Duplex (print)
import Type.Proxy (Proxy(..))
import Web.DOM.Element (clientHeight, scrollHeight)
import Web.Event.Event (Event, preventDefault)
import Web.HTML.HTMLElement as HTMLE
import Web.UIEvent.MouseEvent as ME

data Action
  = InitComponent
  | NavigateToRoute Event Route

type Slot id = forall q. H.Slot q Void id

type ChildSlots =
  ( eventPreviews :: EventPrvComp.Slot Int
  , contentComp :: ContentComp.Slot Unit
  )

type State =
  { venue :: Venue
  , isExpanded :: Boolean
  }

component :: forall q o m. MonadEffect m => Navigate m => H.Component q Venue o m
component =
  H.mkComponent
    { initialState: \s -> { venue : s, isExpanded : false }
    , render
    , eval: H.mkEval H.defaultEval { handleAction = handleAction, initialize = Just InitComponent }
    }

addressInLine :: Address -> String
addressInLine (Address address) =
  S.joinWith ", " $ 
    [address.addressLine1]
      <> A.fromFoldable (address.addressLine2)
      <> A.fromFoldable (address.addressLine3)
      <> [address.addressLocality]
      <> A.fromFoldable (address.addressCounty)
      <> [address.addressPostalCode]
      <> A.fromFoldable (address.addressCountry)

cardContentRef ∷ H.RefLabel
cardContentRef = H.RefLabel "card-content-ref"

render :: forall m. MonadEffect m => State -> H.ComponentHTML Action ChildSlots m
render s = 
  HH.div [ HP.classes [ Bulma.card ], HP.style "height:100%; display:flex; flex-direction:column; max-height: 625px" ] $
    maybeImage <>
    [ HH.div [ HP.ref cardContentRef, HP.classes [ Bulma.cardContent ], HP.style "margin-bottom:auto; height:100%; overflow: hidden" ] $
      [ HH.p [ HP.classes [ Bulma.title ] ]
        [ HH.a [ HP.href (print routeCodec (VenueRoute v.id)), HE.onClick \e -> NavigateToRoute (ME.toEvent e) (VenueRoute v.id) ] [HH.text v.name]
        ]
      , HH.p_ [renderIcon "fa-building" (HH.text $ addressInLine v.address)]
      ] <> A.fromFoldable ( map (\pn -> HH.p_ [renderIcon "fa-phone" (HH.a [ HP.href $ "tel:" <> pn ] [HH.text pn]) ] )  v.phoneNumber)
        <> A.fromFoldable ( map (\s -> HH.p_ [renderIcon "fa-globe" (HH.a [HP.href s] [HH.text s])] )  v.website)
        <>  [ HH.div [ HP.classes [ Bulma.block, Bulma.mt3 ] ]
              [ HH.slot_ (Proxy :: _ "contentComp") unit ContentComp.component v.content
              ]
            ]
    , HH.a 
      [ HP.href (print routeCodec (VenueRoute v.id))
      , HE.onClick \e -> NavigateToRoute (ME.toEvent e) (VenueRoute v.id)
      , HP.classes $ showMoreVisisibleClasses <> [ Bulma.ml5 ] 
      ] [ HH.text "...show more" ] 
    ]
  where
    showMoreVisisibleClasses = 
      if s.isExpanded then
        []
      else 
        [ Bulma.isInvisible ]
    (Venue v) = s.venue
    maybeImage =
      case v.image of
        Just imSrc -> 
          [ HH.div [ HP.classes [ Bulma.cardImage ] ]
            [ HH.figure [ HP.classes [ Bulma.image ] ] 
              [ HH.img [ HP.src imSrc, HP.classes [ H.ClassName "hereabout-clip" ] ]
              ]
            ]
          ]
        Nothing -> []

handleAction :: forall cs o m. MonadEffect m => Navigate m => Action → H.HalogenM State Action cs o m Unit
handleAction = case _ of
  InitComponent -> do
    maybeCardContent <- H.getHTMLElementRef $ cardContentRef
    case maybeCardContent of
      Just cardContent -> do
        let cce = HTMLE.toElement cardContent
        sh <- liftEffect $ scrollHeight cce
        ch <- liftEffect $ clientHeight cce
        if (sh > ch) then
          H.modify_ _{ isExpanded = true }
        else
          pure unit
      Nothing -> pure unit
  NavigateToRoute me route -> do
    H.liftEffect $ preventDefault me
    H.lift $ navigate route

